"use client";

import { useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Fade,
  Zoom,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Slider,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Movie,
  Close,
  CheckCircle,
  Gif,
} from "@mui/icons-material";

interface GifFile {
  file: File;
  id: string;
}

const QUALITY_OPTIONS = [
  { value: "28", label: "High Quality (slower)" },
  { value: "32", label: "Medium Quality (balanced)" },
  { value: "36", label: "Low Quality (faster, smaller)" },
];

const FPS_OPTIONS = [
  { value: "original", label: "Original" },
  { value: "15", label: "15 FPS" },
  { value: "24", label: "24 FPS" },
  { value: "30", label: "30 FPS" },
  { value: "60", label: "60 FPS" },
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export default function GifToMp4Converter() {
  const [gifFile, setGifFile] = useState<GifFile | null>(null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [quality, setQuality] = useState("32");
  const [fps, setFps] = useState("original");
  const [loop, setLoop] = useState(1);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError(
        `File size exceeds 50MB limit. Please select a smaller GIF file.`
      );
      return;
    }

    // Check if it's a GIF file
    if (
      file.type !== "image/gif" &&
      !file.name.toLowerCase().endsWith(".gif")
    ) {
      setError(`${file.name} is not a GIF file.`);
      return;
    }

    setGifFile({
      file,
      id: Math.random().toString(36).substr(2, 9),
    });
    setError(null);
    setSuccess(false);
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setGifFile(null);
    setError(null);
    setSuccess(false);
    const fileInput = document.getElementById(
      "gif-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleQualityChange = (event: SelectChangeEvent) => {
    setQuality(event.target.value);
  };

  const handleFpsChange = (event: SelectChangeEvent) => {
    setFps(event.target.value);
  };

  const handleLoopChange = (_event: Event, newValue: number | number[]) => {
    setLoop(newValue as number);
  };

  const convertToMp4 = async () => {
    if (!gifFile) {
      setError("Please select a GIF file to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append("gif", gifFile.file);
      formData.append("quality", quality);
      formData.append("fps", fps);
      formData.append("loop", loop.toString());

      const response = await axios.post(
        process.env.NEXT_PUBLIC_MEDIA_CONVERTER_API + "/gif-to-mp4",
        formData,
        {
          timeout: 600000,
          responseType: "blob",
        }
      );

      // Get the video blob
      const blob = new Blob([response.data]);

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const originalName = gifFile.file.name.replace(/\.[^/.]+$/, "");
      a.download = `${originalName}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(true);

      // Clear file after successful conversion
      setTimeout(() => {
        removeFile();
      }, 2000);
    } catch (err) {
      let errorMessage = "Failed to convert GIF to MP4";
      if (axios.isAxiosError(err) && err.response) {
        try {
          const contentType = String(err.response.headers["content-type"] ?? "");
          if (contentType.includes("application/json")) {
            const errorText = await new Response(err.response.data).text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          } else {
            errorMessage = `Server error: ${err.response.status}`;
          }
        } catch {
          errorMessage = err.message || errorMessage;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: "3px dashed",
            borderColor: dragActive ? "primary.main" : "grey.300",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            bgcolor: dragActive ? "primary.50" : "grey.50",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.50",
            },
          }}
          onClick={() => document.getElementById("gif-file-input")?.click()}
        >
          <input
            id="gif-file-input"
            type="file"
            accept="image/gif,.gif"
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: "none" }}
          />

          <CloudUpload
            sx={{
              fontSize: 80,
              color: dragActive ? "primary.main" : "grey.400",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            component="p"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            {dragActive
              ? "Drop your GIF here"
              : "Click to upload or drag and drop"}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Supported format: GIF
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Maximum file size: 50MB
          </Typography>

          <Button
            variant="contained"
            startIcon={<Gif sx={{ color: "#ffffff" }} />}
            sx={{
              mt: 3,
              color: "#ffffff",
              backgroundColor: "#5558d3",
              "&:hover": {
                backgroundColor: "#4448c2",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById("gif-file-input")?.click();
            }}
          >
            Select GIF
          </Button>
        </Box>

        {/* Output Options */}
        <Fade in={true}>
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h6"
              component="p"
              fontWeight={600}
              sx={{ mb: 2 }}
            >
              Output Options
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <FormControl fullWidth>
                <InputLabel id="quality-label">Video Quality</InputLabel>
                <Select
                  labelId="quality-label"
                  value={quality}
                  label="Video Quality"
                  onChange={handleQualityChange}
                >
                  {QUALITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="fps-label">Frame Rate</InputLabel>
                <Select
                  labelId="fps-label"
                  value={fps}
                  label="Frame Rate"
                  onChange={handleFpsChange}
                >
                  {FPS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Box sx={{ px: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Loop Count:{" "}
                {loop === 0
                  ? "Infinite"
                  : loop === 1
                    ? "No loop (play once)"
                    : `${loop} times`}
              </Typography>
              <Slider
                value={loop}
                onChange={handleLoopChange}
                min={0}
                max={10}
                step={1}
                marks={[
                  { value: 0, label: "∞" },
                  { value: 1, label: "1" },
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                ]}
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>
        </Fade>

        {/* Selected File Preview */}
        {gifFile && (
          <Fade in={!!gifFile}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  Selected GIF
                </Typography>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={removeFile}
                  color="error"
                  variant="outlined"
                >
                  Remove
                </Button>
              </Box>

              <Zoom in>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: "#E91E63",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Gif sx={{ fontSize: 32, color: "white" }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {gifFile.file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(gifFile.file.size)}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={removeFile}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "white",
                      },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Paper>
              </Zoom>
            </Box>
          </Fade>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            GIF converted to MP4 successfully! Your download should start
            automatically.
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Movie sx={{ color: "#ffffff" }} />}
            onClick={convertToMp4}
            disabled={converting || !gifFile}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting ? "Converting..." : "Convert to MP4"}
          </Button>

          {gifFile && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<Gif />}
                label="GIF File"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={formatFileSize(gifFile.file.size)}
                variant="outlined"
              />
              <Chip
                icon={<Movie />}
                label="MP4"
                color="secondary"
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
